/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: Service Architecture
 * PURPOSE: Central orchestrator for service lifecycle management with dependency-aware initialization
 *
 * NEW PATTERNS IMPLEMENTED:
 * - BaseService Pattern: Lifecycle management (IDLE → INITIALIZING → INITIALIZED/FAILED)
 * - Dependency Graph: Topological sort for initialization ordering
 * - Type-Safe Registry: Symbol-based service keys with phantom types
 * - Parallel Initialization: Concurrent service startup (~700ms savings)
 *
 * KEY FEATURES:
 * - Automatic dependency resolution using DependencyGraph
 * - Critical vs non-critical service handling
 * - Lazy service support (on-demand initialization)
 * - Level-based initialization with parallel support
 * - Performance metrics tracking (totalTime, levelTimes, serviceErrors)
 * - Configurable parallel/sequential initialization modes
 *
 * RELATED FILES:
 * - src/lib/services/core/BaseService.ts
 * - src/lib/services/core/registry.ts
 * - src/lib/services/core/dependency-graph.ts
 */

// services/core/ServiceManager.ts
import { BaseService } from './BaseService';
import { createLogger } from '$lib/utils/logger';
import { unifiedDataUpdateOrchestrator } from '$lib/services/orchestration';
import { serviceRegistry, type ServiceKey, type ServiceDefinition, SERVICE_KEYS } from './registry';
import { DependencyGraph } from './dependency-graph';

const logger = createLogger('core.service-manager');

/**
 * Base interface that all services must implement
 * Provides optional lifecycle methods for ServiceManager
 */
interface IService {
	initialize?: () => Promise<boolean> | Promise<void> | void;
	reset?: () => void | Promise<void>;
	isInitialized?: () => boolean;
}

/**
 * Service Manager for controlled initialization of services
 *
 * Phase 1.3 Refactoring:
 * - Uses Symbol-based ServiceRegistry instead of string Map
 * - Uses DependencyGraph for automatic initialization ordering
 * - ServiceDefinition metadata for critical/lazy/priority flags
 */
export class ServiceManager {
	private static instance: ServiceManager;
	// Services are now stored in serviceRegistry, not here

	// Phase 1.4: Performance metrics tracking
	private initializationMetrics = {
		totalTime: 0,
		levelTimes: [] as Array<{ level: number; services: number; time: number; parallel: boolean }>,
		serviceErrors: [] as Array<{ service: string; error: string; critical: boolean }>
	};

	// Phase 1.4: Backward compatibility flag for parallel initialization
	private enableParallelInit = true; // Set to false to use sequential initialization

	// Service definitions with full metadata (replaces old serviceDependencies)
	private readonly serviceDefinitions: Map<symbol, ServiceDefinition> = new Map([
		// ========================================================================
		// CORE SERVICES (critical: true - app won't start if these fail)
		// ========================================================================
		[
			SERVICE_KEYS.APP,
			{
				key: SERVICE_KEYS.APP,
				dependencies: [],
				critical: true,
				lazy: false,
				priority: 0
			}
		],
		[
			SERVICE_KEYS.AUTH,
			{
				key: SERVICE_KEYS.AUTH,
				dependencies: [SERVICE_KEYS.APP],
				critical: true,
				lazy: false,
				priority: 1
			}
		],
		[
			SERVICE_KEYS.WEBSOCKET,
			{
				key: SERVICE_KEYS.WEBSOCKET,
				dependencies: [SERVICE_KEYS.AUTH],
				critical: true,
				lazy: false,
				priority: 2
			}
		],

		// ========================================================================
		// DATA SERVICES (critical: false - app can run without them)
		// ========================================================================
		[
			SERVICE_KEYS.ALERTS,
			{
				key: SERVICE_KEYS.ALERTS,
				dependencies: [SERVICE_KEYS.WEBSOCKET],
				critical: false,
				lazy: false,
				priority: 3
			}
		],
		[
			SERVICE_KEYS.WORK_ORDERS,
			{
				key: SERVICE_KEYS.WORK_ORDERS,
				dependencies: [SERVICE_KEYS.WEBSOCKET],
				critical: false,
				lazy: false,
				priority: 3
			}
		],

		// ========================================================================
		// PRESENCE & NOTIFICATIONS
		// ========================================================================
		[
			SERVICE_KEYS.UNIFIED_PRESENCE,
			{
				key: SERVICE_KEYS.UNIFIED_PRESENCE,
				dependencies: [SERVICE_KEYS.WEBSOCKET],
				critical: false,
				lazy: false,
				priority: 3
			}
		],
		[
			SERVICE_KEYS.NOTIFICATION,
			{
				key: SERVICE_KEYS.NOTIFICATION,
				dependencies: [SERVICE_KEYS.WEBSOCKET],
				critical: false,
				lazy: false,
				priority: 3
			}
		],

		// ========================================================================
		// ORCHESTRATION
		// ========================================================================
		[
			SERVICE_KEYS.DATA_UPDATE_ORCHESTRATOR,
			{
				key: SERVICE_KEYS.DATA_UPDATE_ORCHESTRATOR,
				dependencies: [SERVICE_KEYS.WEBSOCKET],
				critical: false,
				lazy: false,
				priority: 3
			}
		],

		// ========================================================================
		// ADMIN SERVICES (lazy: true - load on demand)
		// ========================================================================
		[
			SERVICE_KEYS.ORGANIZATIONS,
			{
				key: SERVICE_KEYS.ORGANIZATIONS,
				dependencies: [],
				critical: false,
				lazy: true,
				priority: 10
			}
		],
		[
			SERVICE_KEYS.DEPARTMENTS,
			{
				key: SERVICE_KEYS.DEPARTMENTS,
				dependencies: [SERVICE_KEYS.ORGANIZATIONS],
				critical: false,
				lazy: true,
				priority: 11
			}
		],
		[
			SERVICE_KEYS.ROLES,
			{
				key: SERVICE_KEYS.ROLES,
				dependencies: [],
				critical: false,
				lazy: true,
				priority: 10
			}
		],
		[
			SERVICE_KEYS.PERMISSIONS,
			{
				key: SERVICE_KEYS.PERMISSIONS,
				dependencies: [SERVICE_KEYS.ROLES],
				critical: false,
				lazy: true,
				priority: 11
			}
		],
		[
			SERVICE_KEYS.PAGES,
			{
				key: SERVICE_KEYS.PAGES,
				dependencies: [],
				critical: false,
				lazy: true,
				priority: 10
			}
		],
		[
			SERVICE_KEYS.ADMIN_DASHBOARD,
			{
				key: SERVICE_KEYS.ADMIN_DASHBOARD,
				dependencies: [SERVICE_KEYS.AUTH],
				critical: false,
				lazy: true,
				priority: 10
			}
		],
		[
			SERVICE_KEYS.OPA,
			{
				key: SERVICE_KEYS.OPA,
				dependencies: [SERVICE_KEYS.AUTH],
				critical: false,
				lazy: true,
				priority: 10
			}
		]
	]);

	private initialized = false;

	private constructor() {}

	/**
	 * Get the singleton instance of the manager
	 */
	public static getInstance(): ServiceManager {
		if (!ServiceManager.instance) {
			ServiceManager.instance = new ServiceManager();
		}
		return ServiceManager.instance;
	}

	/**
	 * Get initialization performance metrics
	 * Phase 1.4: Performance measurement and observability
	 *
	 * @returns Initialization metrics with timing and error data
	 */
	public getInitializationMetrics() {
		return { ...this.initializationMetrics };
	}

	/**
	 * Reset initialization metrics
	 * Phase 1.4: Cleanup for fresh measurements
	 */
	private resetMetrics() {
		this.initializationMetrics = {
			totalTime: 0,
			levelTimes: [],
			serviceErrors: []
		};
	}

	/**
	 * Set parallel initialization mode (for backward compatibility)
	 * Phase 1.4: Runtime control over parallel vs sequential initialization
	 *
	 * @param enabled - True to enable parallel initialization, false for sequential
	 */
	public setParallelInitialization(enabled: boolean): void {
		this.enableParallelInit = enabled;
		logger.debug('Parallel initialization mode changed', {
			enabled,
			mode: enabled ? 'parallel' : 'sequential'
		});
	}

	/**
	 * Register a service with the manager
	 * Phase 1.3: Now uses Symbol-based keys for type safety
	 *
	 * @param key - Type-safe service key from SERVICE_KEYS
	 * @param service - Service instance (type is inferred from key!)
	 *
	 * @example
	 * ```typescript
	 * serviceManager.registerService(SERVICE_KEYS.AUTH, authService);
	 * // TypeScript knows authService must match AUTH type!
	 * ```
	 */
	public registerService<T extends IService>(key: ServiceKey<T>, service: T): void {
		const definition = this.serviceDefinitions.get(key as symbol);
		if (!definition) {
			throw new Error(`No definition found for service: ${String(key)}`);
		}

		serviceRegistry.register(key, service, definition);
		logger.debug('Service registered', {
			service: String(key),
			critical: definition.critical,
			lazy: definition.lazy,
			priority: definition.priority,
			dependencyCount: definition.dependencies.length
		});
	}

	/**
	 * Get a service by key (type-safe)
	 * Phase 1.3: Now uses Symbol-based keys with full type inference
	 *
	 * @param key - Type-safe service key from SERVICE_KEYS
	 * @returns The service instance (type is inferred!)
	 *
	 * @example
	 * ```typescript
	 * const auth = serviceManager.getService(SERVICE_KEYS.AUTH);
	 * // TypeScript knows auth is AuthService!
	 * ```
	 */
	public getService<T>(key: ServiceKey<T>): T {
		return serviceRegistry.get(key);
	}

	/**
	 * Get all registered service keys
	 * Phase 1.3: Returns Symbol keys instead of Map
	 *
	 * @returns Array of service keys
	 */
	public getAllServices(): symbol[] {
		return serviceRegistry.getAllKeys();
	}

	/**
	 * Check if a service is registered
	 * Phase 1.3: Now uses Symbol-based keys
	 *
	 * @param key - Service key to check
	 * @returns True if the service is registered
	 */
	public hasService(key: symbol): boolean {
		return serviceRegistry.has(key);
	}

	/**
	 * Initialize all services using DependencyGraph
	 * Phase 1.3: Automatic dependency resolution with topological sort
	 *
	 * Features:
	 * - Automatic initialization ordering via DependencyGraph
	 * - Circular dependency detection (fails fast)
	 * - Critical vs non-critical service handling
	 * - Lazy service support (skipped in initialization)
	 * - Level-based initialization (Phase 1.4 will add parallelization)
	 *
	 * @returns Promise that resolves when all services are initialized
	 * @throws {Error} If critical service fails or circular dependency detected
	 */
	public async initialize(): Promise<void> {
		if (this.initialized) {
			logger.debug('Initialization skipped - already initialized');
			return;
		}

		try {
			const initStart = performance.now();
			this.resetMetrics(); // Clear previous metrics

			logger.info('Service initialization started', {
				parallelMode: this.enableParallelInit
			});

			// Step 1: Build dependency graph
			const graph = new DependencyGraph();

			// Helper: Check if all dependencies are registered
			const allDependenciesRegistered = (deps: symbol[]): boolean => {
				return deps.every((dep) => serviceRegistry.has(dep));
			};

			for (const [key, definition] of this.serviceDefinitions.entries()) {
				// Only add services that are:
				// 1. Non-lazy
				// 2. Actually registered
				// 3. Have all dependencies registered (or have no dependencies)
				if (
					!definition.lazy &&
					serviceRegistry.has(key) &&
					allDependenciesRegistered(definition.dependencies)
				) {
					graph.addNode(definition);
				}
			}

			// Step 2: Get initialization order (throws if circular dependencies)
			let initializationLevels: symbol[][];
			try {
				initializationLevels = graph.getInitializationLevels();
			} catch (error: any) {
				logger.error('Circular dependency detected in service definitions', {
					error: error.message,
					phase: 'dependency-analysis'
				});
				throw error;
			}

			logger.info('Initialization order determined', {
				levelCount: initializationLevels.length,
				totalServices: initializationLevels.flat().length
			});

			// Step 3: Initialize services level by level
			for (let level = 0; level < initializationLevels.length; level++) {
				const servicesInLevel = initializationLevels[level];
				const levelStart = performance.now();

				logger.debug('Level initialization started', {
					level,
					serviceCount: servicesInLevel.length,
					services: servicesInLevel.map((s) => String(s))
				});

				// Phase 1.4: Choose parallel or sequential based on flag and service count
				if (this.enableParallelInit && servicesInLevel.length > 1) {
					// PARALLEL INITIALIZATION (Phase 1.4)

					// Create initialization promises for all services in this level
					const initPromises = servicesInLevel.map(async (serviceKey) => {
						const definition = this.serviceDefinitions.get(serviceKey);
						if (!definition) {
							return { success: false, serviceKey, error: 'No definition found' };
						}

						try {
							const service = serviceRegistry.get(serviceKey as any) as IService;

							if (service && typeof service.initialize === 'function') {
								const serviceStart = performance.now();

								await service.initialize();

								const serviceTime = performance.now() - serviceStart;
								logger.debug('Service initialized', {
									level,
									service: String(serviceKey),
									duration: serviceTime,
									mode: 'parallel'
								});

								return { success: true, serviceKey, time: serviceTime };
							}

							return { success: true, serviceKey, skipped: true };
						} catch (error: any) {
							const errorMsg = `Failed to initialize service ${String(serviceKey)}: ${error.message}`;

							// Store error in metrics
							this.initializationMetrics.serviceErrors.push({
								service: String(serviceKey),
								error: error.message,
								critical: definition.critical
							});

							if (definition.critical) {
								logger.error('Critical service initialization failed', {
									level,
									service: String(serviceKey),
									error: error.message,
									critical: true,
									mode: 'parallel'
								});
								return { success: false, serviceKey, error: errorMsg, critical: true };
							} else {
								logger.warn('Non-critical service initialization failed', {
									level,
									service: String(serviceKey),
									error: error.message,
									critical: false,
									mode: 'parallel'
								});
								return { success: false, serviceKey, error: errorMsg, critical: false };
							}
						}
					});

					// Wait for all services in this level to complete
					const results = await Promise.allSettled(initPromises);

					// Process results
					let criticalFailure = false;
					for (const result of results) {
						if (result.status === 'fulfilled' && result.value.success === false) {
							if (result.value.critical) {
								criticalFailure = true;
								break;
							}
						} else if (result.status === 'rejected') {
							// Promise itself rejected (shouldn't happen with our error handling, but be safe)
							logger.error('Unexpected promise rejection during parallel initialization', {
								level,
								reason: result.reason
							});
							criticalFailure = true;
							break;
						}
					}

					// Calculate level time
					const levelTime = performance.now() - levelStart;
					this.initializationMetrics.levelTimes.push({
						level,
						services: servicesInLevel.length,
						time: levelTime,
						parallel: true
					});

					logger.info('Level initialization completed', {
						level,
						serviceCount: servicesInLevel.length,
						duration: levelTime,
						mode: 'parallel'
					});

					// If critical service failed, abort initialization
					if (criticalFailure) {
						logger.error('Critical service failure - aborting initialization', {
							level,
							phase: 'parallel-initialization'
						});
						throw new Error(`Critical service initialization failed at level ${level}`);
					}
				} else {
					// SEQUENTIAL INITIALIZATION (Phase 1.3 fallback)

					for (const serviceKey of servicesInLevel) {
						const definition = this.serviceDefinitions.get(serviceKey);
						if (!definition) continue;

						try {
							// Get service from registry
							const service = serviceRegistry.get(serviceKey as any) as IService;

							// Call initialize if it exists (BaseService pattern)
							if (service && typeof service.initialize === 'function') {
								const serviceStart = performance.now();
								await service.initialize();
								logger.debug('Service initialized', {
									level,
									service: String(serviceKey),
									duration: performance.now() - serviceStart,
									mode: 'sequential'
								});
							}
						} catch (error: any) {
							const errorMsg = `Failed to initialize service ${String(serviceKey)}: ${error.message}`;

							// Store error in metrics
							this.initializationMetrics.serviceErrors.push({
								service: String(serviceKey),
								error: error.message,
								critical: definition.critical
							});

							if (definition.critical) {
								// Critical service failure - abort initialization
								logger.error('Critical service initialization failed', {
									level,
									service: String(serviceKey),
									error: error.message,
									critical: true,
									mode: 'sequential'
								});
								throw new Error(`Critical service initialization failed: ${errorMsg}`);
							} else {
								// Non-critical service failure - log and continue
								logger.warn('Non-critical service initialization failed', {
									level,
									service: String(serviceKey),
									error: error.message,
									critical: false,
									mode: 'sequential'
								});
							}
						}
					}

					// Calculate level time
					const levelTime = performance.now() - levelStart;
					this.initializationMetrics.levelTimes.push({
						level,
						services: servicesInLevel.length,
						time: levelTime,
						parallel: false
					});

					logger.info('Level initialization completed', {
						level,
						serviceCount: servicesInLevel.length,
						duration: levelTime,
						mode: 'sequential'
					});
				}
			}

			this.initialized = true;

			// Phase 1.4: Calculate and log total time with detailed metrics
			const totalTime = performance.now() - initStart;
			this.initializationMetrics.totalTime = totalTime;

			logger.info('Service initialization completed successfully', {
				totalDuration: totalTime,
				levelCount: this.initializationMetrics.levelTimes.length,
				levels: this.initializationMetrics.levelTimes.map((l) => ({
					level: l.level,
					services: l.services,
					duration: l.time,
					mode: l.parallel ? 'parallel' : 'sequential'
				})),
				errorCount: this.initializationMetrics.serviceErrors.length
			});

			// Log warnings if any services failed
			if (this.initializationMetrics.serviceErrors.length > 0) {
				logger.warn('Some services failed during initialization', {
					failedCount: this.initializationMetrics.serviceErrors.length,
					failures: this.initializationMetrics.serviceErrors
				});
			}
		} catch (error: any) {
			logger.error('Service initialization failed', {
				error: error.message,
				phase: 'initialization'
			});
			throw error;
		}
	}

	/**
	 * Initialize a lazy-loaded service on demand
	 * Phase 1.3: Support for lazy service initialization
	 *
	 * @param key - Service key to initialize
	 * @returns Initialized service instance
	 *
	 * @example
	 * ```typescript
	 * // Lazy-load admin service when needed
	 * const orgs = await serviceManager.initializeLazyService(SERVICE_KEYS.ORGANIZATIONS);
	 * ```
	 */
	public async initializeLazyService<T extends IService>(key: ServiceKey<T>): Promise<T> {
		const definition = this.serviceDefinitions.get(key as symbol);
		if (!definition) {
			throw new Error(`No definition found for service: ${String(key)}`);
		}

		if (!definition.lazy) {
			// Not a lazy service, should already be initialized
			return serviceRegistry.get(key);
		}

		const service = serviceRegistry.get(key);

		// Check if already initialized
		if (service && typeof service.initialize === 'function') {
			// Check isInitialized if available (BaseService pattern)
			const hasIsInitialized = typeof (service as any).isInitialized === 'function';
			if (!hasIsInitialized || !(service as any).isInitialized()) {
				const startTime = performance.now();
				await service.initialize();
				logger.info('Lazy service initialized', {
					service: String(key),
					duration: performance.now() - startTime,
					lazy: true
				});
			}
		}

		return service;
	}

	/**
	 * Get service definition metadata
	 * Phase 1.3: Access to service configuration
	 *
	 * @param key - Service key
	 * @returns Service definition or undefined
	 */
	public getServiceDefinition(key: symbol): ServiceDefinition | undefined {
		return this.serviceDefinitions.get(key);
	}

	/**
	 * Reset all services (cleanup on logout/reset)
	 * Phase 1.3: Uses serviceRegistry instead of local Map
	 */
	public async resetAll(): Promise<void> {
		const allKeys = serviceRegistry.getAllKeys();
		const errors: Array<{ service: string; error: string }> = [];

		for (const key of allKeys) {
			try {
				const service = serviceRegistry.get(key as any) as IService;
				if (service && typeof service.reset === 'function') {
					service.reset();
				}
			} catch (error: any) {
				errors.push({
					service: String(key),
					error: error.message
				});
				logger.warn('Error resetting service', {
					service: String(key),
					error: error.message
				});
			}
		}

		this.initialized = false;
		logger.info('All services reset', {
			serviceCount: allKeys.length,
			errorCount: errors.length,
			errors: errors.length > 0 ? errors : undefined
		});
	}
}

export const serviceManager = ServiceManager.getInstance();
export default serviceManager;
